import type { CheckInOutType } from '@vrplatform/components';
import { type RequestQuery, useInfiniteApiQuery } from '@vrplatform/data';
import { type Maybe, isUUID } from '@vrplatform/utils';
import { useReservationsTableFilter } from './useReservationsTableFilter';

type Props = {
  hideCancellations?: boolean;
  excludeReservationIds?: string[];
  listingId?: string;
};

export type ReservationTableQueryData = NonNullable<
  ReturnType<typeof useReservationsTableQuery>['data']
>;

export type ReservationRow =
  ReservationTableQueryData['pages'][number]['list'][number];

type Filter = ReturnType<typeof useReservationsTableFilter>['filter'];

export const STORYBOOK_RESERVATIONS_TABLE_KEY = 'reservations-table';

export const getReservationsTableQueryFilter = ({
  filter,
  limit,
  page,
  props,
}: {
  filter: Filter;
  limit: number;
  page: number;
  props: Props | undefined;
}): RequestQuery<'get:/reservations'> => {
  const dateType = (filter.dateType as Maybe<CheckInOutType>) ?? undefined;

  const paidStatus =
    (filter.paidStatus as Maybe<'paid' | 'unpaid'>) ?? undefined;

  const isSearchUuid = isUUID(filter.search || '');

  return {
    limit,
    page,
    date: filter.date ?? undefined,
    dateField: !filter.date
      ? undefined
      : dateType === 'all'
        ? 'intersection'
        : dateType,
    status: props?.hideCancellations
      ? 'booked'
      : ((filter.status as any) ?? undefined),
    ids: props?.excludeReservationIds?.length
      ? `!${props.excludeReservationIds.join(',')}`
      : isSearchUuid
        ? filter.search!
        : undefined,
    search: isSearchUuid ? undefined : filter.search?.trim(),
    bookingChannel: filter.bookingChannels?.join(','),
    connectionId: filter.connectionIds?.join(','),
    listingId: props?.listingId ?? filter.listingIds?.join(','),
    paidStatus: paidStatus
      ? paidStatus === 'paid'
        ? 'paid'
        : '!paid'
      : undefined,
    generalLedgerStatus: filter.generalLedgerStatus ?? undefined,
    businessModel: filter.businessModel ?? undefined,
    lineType: filter.lineType ?? undefined,
  };
};

type App = {
  id: string;
  name: string;
  icon: string | undefined;
};

type PageParam = {
  page: number;
  apps: App[];
};

export const useReservationsTableQuery = (props?: Props) => {
  const { filter } = useReservationsTableFilter();

  return useInfiniteApiQuery(
    async ($api, signal, args, pageParam: PageParam, hasura) => {
      const { page, apps: initialApps } = pageParam;

      const apps =
        page === 1
          ? await hasura((q) =>
              q.app({}).map((app) => ({
                id: app.id,
                name: app.name,
                icon: app.iconRound,
              }))
            )
          : initialApps;

      const result = await $api.GET('/reservations', {
        params: {
          query: getReservationsTableQueryFilter({
            props: args.props,
            filter: args.filter,
            limit: args.pageLimit,
            page,
          }),
        },
        signal,
      });

      if (result.error) throw result.error;

      const list = result.data?.data || [];

      return {
        list: list.map((res) => ({
          ...res,
          connection: !res.connection
            ? res.connection
            : {
                ...res.connection,
                app: apps.find((app) => app.id === res.connection?.appId),
              },
        })),
        aggregate: result.data?.pagination.total ?? 0,
        pagination: result.data?.pagination,
        apps,
      };
    },
    {
      variables: {
        filter,
        pageLimit: 50,
        props,
      },
      queryGroup: 'reservations',
      uniqueQueryKey: 'reservations-table',
      queryKeys: ['reservations-table'],
      initialPageParam: {
        page: 1,
        apps: [] as App[],
      },
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.pagination.nextPage ?? null;

        if (!nextPage) return null;

        return {
          page: nextPage,
          apps: lastPage.apps,
        };
      },
      storybookQueryKey: STORYBOOK_RESERVATIONS_TABLE_KEY,
    }
  );
};
